export type ProspectStatus =
  | 'new'
  | 'enriching'
  | 'audit_active'
  | 'audited'
  | 'draft_ready'
  | 'approved'
  | 'pushed'
  | 'active'
  | 'replied'
  | 'bounced'
  | 'unsubscribed'
  | 'completed'
  | 'failed';

export type BadgeTone =
  | 'primary'
  | 'success'
  | 'error'
  | 'warning'
  | 'info'
  | 'neutral';

export const PROSPECT_STATUSES: Array<{
  value: ProspectStatus;
  label: string;
  tone: BadgeTone;
}> = [
  { value: 'new', label: 'New', tone: 'neutral' },
  { value: 'enriching', label: 'Enriching', tone: 'info' },
  { value: 'audit_active', label: 'Audit active', tone: 'info' },
  { value: 'audited', label: 'Audited', tone: 'primary' },
  { value: 'draft_ready', label: 'Draft ready', tone: 'warning' },
  { value: 'approved', label: 'Approved', tone: 'primary' },
  { value: 'pushed', label: 'Pushed', tone: 'success' },
  { value: 'active', label: 'Active', tone: 'success' },
  { value: 'replied', label: 'Replied', tone: 'success' },
  { value: 'bounced', label: 'Bounced', tone: 'error' },
  { value: 'unsubscribed', label: 'Unsubscribed', tone: 'error' },
  { value: 'completed', label: 'Completed', tone: 'neutral' },
  { value: 'failed', label: 'Failed', tone: 'error' },
];

export const prospectStatusMeta = (status?: string | null) =>
  PROSPECT_STATUSES.find((s) => s.value === status) ?? {
    value: 'new' as ProspectStatus,
    label: status || '—',
    tone: 'neutral' as BadgeTone,
  };

export interface ProspectListItem {
  id: number;
  name: string;
  restaurant_group_name?: string;
  phone?: string;
  contact_name?: string;
  contact_first_name?: string;
  contact_last_name?: string;
  contact_email?: string;
  contact_title?: string;
  formatted_address?: string;
  city?: string;
  market?: string;
  cuisine?: string;
  neighborhood?: string;
  status: ProspectStatus;
  do_not_contact: boolean;
  last_contacted_at?: string | null;
  source?: string;
  created_at: string;
  updated_at: string;
  audit_call_count: number;
  last_call_date?: string | null;
  last_call_outcome?: string | null;
  latest_missed_call_rate?: string | null;
  competitor_count: number;
  competitor_analysis_status: CompetitorAnalysisStatus;
}

export type CompetitorAnalysisStatus = 'not_run' | 'stale' | 'available';

export interface ProspectCompetitor {
  id: number;
  name: string;
  phone?: string;
  website?: string;
  google_place_id?: string;
  google_maps_url?: string;
  formatted_address?: string;
  latitude?: string | number | null;
  longitude?: string | number | null;
  distance_meters?: number | null;
  types?: string[];
  rating?: number | null;
  user_ratings_total?: number | null;
  price_level?: string;
  business_status?: string;
  match_reason?: string;
  match_confidence?: number | null;
  source?: string;
  last_seen_at?: string;
  created_at?: string;
}

export interface CompetitorAnalysisResponse {
  status: CompetitorAnalysisStatus;
  competitor_count: number;
  last_seen_at?: string | null;
  competitors: ProspectCompetitor[];
  campaign_eligible: boolean | null;
  evidence_gaps: string[];
}

export interface ProspectListResponse {
  results: ProspectListItem[];
  count: number;
  page: number;
  page_size: number;
  total_pages: number;
  status_counts: Record<string, number>;
}

export interface AuditCallRecord {
  id: number;
  call_date: string;
  outcome: string;
  time_to_answer_ms?: number | null;
  duration_ms?: number | null;
  retell_call_status?: string;
  retell_disconnection_reason?: string;
  voicemail_detected?: boolean;
  transcript?: string;
  recording_url?: string;
  classification_notes?: string;
  error?: string;
  scheduled_at?: string;
  dial_started_at?: string | null;
  ended_at?: string | null;
  from_number?: string;
  to_number?: string;
  created_at?: string;
  updated_at?: string;
}

export interface MissedCallMetricRecord {
  id: number;
  window_start: string;
  window_end: string;
  total_calls: number;
  answered_calls: number;
  slow_answer_calls: number;
  missed_calls: number;
  voicemail_calls: number;
  busy_calls: number;
  no_answer_calls: number;
  failed_calls: number;
  missed_call_rate: string;
  avg_time_to_answer_ms?: number | null;
  confidence_score: string;
  evidence_summary: string;
}

export interface EnrichmentSnapshot {
  id: number;
  provider: string;
  source_url?: string;
  external_id?: string;
  extracted_fields: Record<string, unknown>;
  fetched_at: string;
}

export interface ResearchFinding {
  source_type?: string;
  title?: string;
  url?: string;
  snippet?: string;
  relevance?: string;
}

export interface ResearchSignal {
  signal_type?: string;
  source_type?: string;
  source_url?: string;
  evidence_summary?: string;
  sales_angle?: string;
  campaign_hook?: string;
  confidence?: 'high' | 'medium' | 'low' | string;
  risk_level?: 'high' | 'medium' | 'low' | string;
  recommended_campaign_type?: string;
}

export interface CampaignCandidate {
  campaign_key?: string;
  evidence_strength?: string;
  freshness?: string;
  source_quality?: string;
  campaign_fit?: string;
  claim_risk?: string;
  confidence?: number;
  supporting_signals?: string[];
  hook?: string;
  suggested_intro?: string;
  suggested_body_angle?: string;
  claim_safety?: string;
  eligible?: boolean;
  ineligibility_reasons?: string[];
  score_components?: Record<string, number>;
  final_score?: number;
}

export interface ResearchRevenueModel {
  applicable?: boolean;
  tier?: string;
  missed_calls_per_month?: number;
  high_value_inquiry_rate?: number;
  average_inquiry_value?: number;
  close_rate?: number;
  estimated_annual_revenue_at_risk?: number;
  basis?: string;
}

export interface ResearchNote {
  id: number;
  status: 'pending' | 'running' | 'complete' | 'failed';
  summary: string;
  findings: ResearchFinding[];
  signals?: ResearchSignal[];
  campaign_candidates?: CampaignCandidate[];
  recommended_campaign?: string;
  signal_strength?: string;
  revenue_model?: ResearchRevenueModel;
  subject_lines?: string[];
  hooks?: string[];
  needs_more_research?: boolean;
  config_version?: Record<string, unknown>;
  model?: string;
  error?: string;
  completed_at?: string | null;
  created_at: string;
}

export const TEMPLATE_SOURCE_DRIP_TEMPLATE = 'drip_campaign_template';

export interface DripDraftSourceData {
  template_source?: string;
  template_id?: number;
  template_name?: string;
  template_campaign_type_key?: string;
  template_warnings?: string[];
  [key: string]: unknown;
}

export interface DripDraft {
  id: number;
  status: 'draft' | 'approved' | 'pushed' | 'rejected' | 'failed';
  generation_batch_id?: string;
  variant_label?: string;
  campaign_type?: string;
  campaign_label?: string;
  selected_campaign?: Record<string, unknown>;
  source_data?: DripDraftSourceData;
  variant_rationale?: string;
  subject_1: string;
  email_1: string;
  email_2: string;
  email_3: string;
  evidence_summary: string;
  confidence_score: string;
  risk_notes: string;
  error?: string;
  approved_at?: string | null;
  pushed_to_instantly_at?: string | null;
  created_at: string;
}

export interface InstantlySync {
  id: number;
  instantly_lead_id?: string;
  campaign_id?: string;
  lead_email?: string;
  status: string;
  last_sync_source?: string;
  last_synced_at?: string | null;
  pushed_at?: string | null;
  notes?: string;
  error?: string;
}

export interface ProspectDetail {
  id: number;
  name: string;
  restaurant_group_name?: string;
  phone?: string;
  website?: string;
  google_place_id?: string;
  google_maps_url?: string;
  contact_name?: string;
  contact_first_name?: string;
  contact_last_name?: string;
  contact_email?: string;
  contact_title?: string;
  formatted_address?: string;
  address_line_1?: string;
  address_line_2?: string;
  city?: string;
  state?: string;
  postal_code?: string;
  country?: string;
  latitude?: string | number | null;
  longitude?: string | number | null;
  market?: string;
  cuisine?: string;
  neighborhood?: string;
  timezone?: string;
  status: ProspectStatus;
  do_not_contact: boolean;
  suppression_reason?: string;
  suppressed_at?: string | null;
  instantly_lead_id?: string;
  instantly_campaign_id?: string;
  last_contacted_at?: string | null;
  source?: string;
  notes?: string;
  created_at: string;
  updated_at: string;
  audit_calls: AuditCallRecord[];
  latest_metric?: MissedCallMetricRecord | null;
  enrichment_snapshots: EnrichmentSnapshot[];
  research_notes: ResearchNote[];
  drip_drafts: DripDraft[];
  instantly_sync?: InstantlySync | null;
  competitor_count: number;
  competitor_analysis_status: CompetitorAnalysisStatus;
}

export const INSTANTLY_STATUSES = [
  'pending',
  'pushed',
  'active',
  'replied',
  'bounced',
  'unsubscribed',
  'interested',
  'not_interested',
  'completed',
  'failed',
];

/** Mirrors the backend's normalize_phone: keep leading +, strip other non-digits. */
export const normalizePhone = (value?: string | null): string => {
  if (!value) return '';
  const prefix = String(value).trim().startsWith('+') ? '+' : '';
  const digits = String(value).replace(/\D+/g, '');
  return digits ? `${prefix}${digits}` : '';
};

export const normalizeEmail = (value?: string | null): string =>
  (value || '').trim().toLowerCase();

/** Compose first/last contact name, falling back to the legacy full name. */
export const contactDisplayName = (prospect: {
  contact_name?: string;
  contact_first_name?: string;
  contact_last_name?: string;
}): string => {
  const composed = [prospect.contact_first_name, prospect.contact_last_name]
    .filter(Boolean)
    .join(' ')
    .trim();
  return composed || prospect.contact_name || '';
};

/** Backend stores coordinates as Decimal(9,6); Google returns full-precision floats. */
export const roundCoord = (value?: number | null): number | null =>
  value != null && Number.isFinite(value) ? Number(value.toFixed(6)) : null;

export const isValidEmail = (value: string): boolean =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

export const CALL_OUTCOME_TONES: Record<string, BadgeTone> = {
  answered: 'success',
  slow_answer: 'warning',
  voicemail: 'warning',
  busy: 'error',
  no_answer: 'error',
  failed: 'error',
  scheduled: 'neutral',
  initiated: 'info',
  unclear: 'neutral',
};

export const formatOutcome = (outcome?: string | null): string =>
  outcome ? outcome.replace(/_/g, ' ') : '—';

export const formatDurationMs = (ms?: number | null): string => {
  if (ms == null) return '—';
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};

export const formatPercent = (value?: string | number | null): string => {
  if (value == null || value === '') return '—';
  const num = typeof value === 'number' ? value : parseFloat(value);
  if (Number.isNaN(num)) return '—';
  return `${num.toFixed(0)}%`;
};

export interface ProspectPayload {
  name: string;
  restaurant_group_name?: string;
  phone?: string;
  website?: string;
  google_place_id?: string;
  google_maps_url?: string;
  contact_name?: string;
  contact_first_name?: string;
  contact_last_name?: string;
  contact_email?: string;
  contact_title?: string;
  formatted_address?: string;
  address_line_1?: string;
  address_line_2?: string;
  city?: string;
  state?: string;
  postal_code?: string;
  country?: string;
  latitude?: number;
  longitude?: number;
  market?: string;
  cuisine?: string;
  neighborhood?: string;
  timezone?: string;
  source?: string;
  source_payload?: Record<string, unknown>;
  notes?: string;
}
